import { useEffect, useLayoutEffect, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import PortfolioPageLink from './PortfolioPageLink'
import { getProjectBySlug } from './portfolioProjects'
import TechStack from './TechStack'
import './portfolio-work.css'

export default function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>()
  const project = slug ? getProjectBySlug(slug) : undefined
  const backTo = project?.section === 'client' ? '/about#client' : '/about#building'

  useEffect(() => {
    const previous = document.title
    document.title = project ? `${project.name} · Jax` : 'Project not found · Jax'
    return () => { document.title = previous }
  }, [project])

  useLayoutEffect(() => {
    const style = document.documentElement.style
    const previous = style.getPropertyValue('scroll-behavior')
    const priority = style.getPropertyPriority('scroll-behavior')
    style.setProperty('scroll-behavior', 'auto', 'important')
    window.scrollTo({ top: 0, behavior: 'auto' })
    if (previous) style.setProperty('scroll-behavior', previous, priority)
    else style.removeProperty('scroll-behavior')
    document.getElementById('main-content')?.focus({ preventScroll: true })
  }, [slug])

  const shell = (children: ReactNode) => (
    <div className="project-detail">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 z-50 bg-jax-surface border border-jax-link text-jax-link text-sm font-mono px-4 py-2 rounded">
        Skip to content
      </a>
      <div className="h-[2px] bg-jax-accent" />
      <header className="border-b border-jax-navy">
        <nav aria-label="Site navigation" className="project-detail-nav">
          <PortfolioPageLink to="/about" className="project-detail-wordmark">JAX.DEV</PortfolioPageLink>
          <PortfolioPageLink to={backTo} className="project-detail-back">Back to projects</PortfolioPageLink>
        </nav>
      </header>
      <main id="main-content" tabIndex={-1} className="project-detail-main">{children}</main>
    </div>
  )

  if (!project) {
    return shell(<section className="project-detail-hero">
      <h1>Project not found.</h1>
      <p className="project-detail-lead">This project link may have changed.</p>
      <PortfolioPageLink to="/about#building" className="project-detail-launch">Browse projects</PortfolioPageLink>
    </section>)
  }

  const { detail } = project
  return shell(<>
    <section className="project-detail-hero" aria-labelledby="project-title">
      <p className="project-detail-eyebrow">{detail.eyebrow}</p>
      <h1 id="project-title">{project.name}</h1>
      <p className="project-detail-lead">{detail.lead}</p>
      {project.href && <PortfolioPageLink to={project.href} className="project-detail-launch">Open {project.name}</PortfolioPageLink>}
    </section>

    <section className="project-detail-section" aria-labelledby="project-purpose">
      <h2 id="project-purpose">What it’s for</h2>
      <div className="project-detail-prose">{detail.about.map(paragraph => <p key={paragraph}>{paragraph}</p>)}</div>
    </section>

    <section className="project-detail-section" aria-labelledby="project-features">
      <h2 id="project-features">What it does</h2>
      <div className="project-detail-features">
        {detail.features.map(feature => <div key={feature.title}>
          <h3>{feature.title}</h3>
          <p>{feature.text}</p>
        </div>)}
      </div>
    </section>

    <section className="project-detail-section" aria-labelledby="project-focus">
      <h2 id="project-focus">The design focus</h2>
      <div className="project-detail-prose"><p>{detail.focus}</p></div>
    </section>

    <section className="project-detail-section" aria-labelledby="project-stack">
      <h2 id="project-stack">Built with</h2>
      <TechStack stack={project.stack} />
    </section>

    <footer className="project-detail-footer">
      <PortfolioPageLink to={backTo}>{project.section === 'client' ? 'More client work' : 'More personal projects'}</PortfolioPageLink>
    </footer>
  </>)
}
